// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import client from '@Client';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { handleNotificationError } from '@Helpers/notification';
import { FormComment } from '@Molecules/Comments/FormComment';
import { CommentsList } from '@Organisms/Comments/CommentsList/CommentsList';
import { CommentOutput } from 'thehive-sdk';
import { useCurrentUser } from '@Queries/userQueries';
import { useLocalMessage } from '@Helpers/locale';
import { computeEventList } from '@Organisms/Comments/eventList';
import { AlertOutput } from 'thehive-sdk';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';

import './AlertDescriptionSider.scss';

interface AlertDescriptionSiderProps {
    data?: AlertOutput;
    dataComment: CommentOutput[];
    itemsPerPage?: number;
    setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
}

const AlertDescriptionsSider = ({
    data,
    dataComment,
    itemsPerPage = 20,
    setItemsPerPage,
}: AlertDescriptionSiderProps): ReactElement | null => {
    const local = useLocalMessage();
    const { _id: alertId = '' } = data || {};
    const { data: currentUser } = useCurrentUser();
    const scrollElement = useRef<HTMLDivElement>(null);

    const { permissions = [], login } = currentUser || {};

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CommentController,
                objectId: alertId,
                parentType: 'alert',
                onError: handleNotificationError,
            }),
        [alertId],
    );

    const onLoadCommentHandler = useCallback(
        (page: number) => {
            setItemsPerPage((prevItems) => prevItems + page * prevItems);
        },
        [setItemsPerPage],
    );

    const readOnly = !(permissions as string[]).includes('manageComment');
    useEffect(() => {
        const view = scrollElement.current;
        if (view && dataComment.length) {
            setTimeout(() => {
                view.scrollTop = view.scrollHeight;
            }, 100);
        }
    }, [dataComment]);

    return (
        <div className="alert-description__sider">
            <div className="alert-description__comment-content" ref={scrollElement}>
                <TitleSection className="case-description__comment-title">{local('comment.section')}</TitleSection>
                {dataComment.length ? (
                    <CommentsList
                        data={dataComment}
                        onDelete={eventList.onDelete}
                        onEdit={eventList.onUpdate}
                        onLoadPage={onLoadCommentHandler}
                        showLoad={dataComment.length >= itemsPerPage}
                        currentUserLogin={login}
                        readOnly={readOnly}
                    />
                ) : (
                    <DisplayEmptyData spin={!data?._id} />
                )}
            </div>
            {readOnly ? null : (
                <div className="alert-description__comment-form">
                    <FormComment placeholder={local('comment.placeholder')} onSubmit={eventList.onCreate} />
                </div>
            )}
        </div>
    );
};

export default AlertDescriptionsSider;
